import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [localSources, setLocalSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          setAuthUser(await res.json());
          const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: 'include' });
          if (adminRes.ok) setIsAdmin((await adminRes.json()).is_admin);
        }
      } catch (e) {
        logError('AuthContext:fetchMe', e); // guest mode, not an error
      }
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setLocalSources(JSON.parse(userData).sources || []);
        } catch (e) {
          logError('AuthContext:parseLocalUser', e);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const isLoggedIn = !!authUser;
  const selectedSources = authUser ? (authUser.selected_sources || []) : localSources;
  const hasConfiguredSources = selectedSources.length > 0;
  const bookmarkedIds = authUser?.bookmarked_story_ids || [];

  const toggleBookmark = useCallback(async (storyId) => {
    if (!authUser) return false;
    const isBookmarked = (authUser.bookmarked_story_ids || []).includes(storyId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/bookmarks/${storyId}`, {
        method: isBookmarked ? 'DELETE' : 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setAuthUser(await res.json());
        return true;
      }
    } catch (e) {
      logError('AuthContext:toggleBookmark', e);
    }
    return false;
  }, [authUser]);

  const recordBubbleBreak = useCallback(async (sourceId) => {
    if (!authUser) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/bubble-break`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_id: sourceId }),
      });
      if (res.ok) setAuthUser(await res.json());
    } catch (e) {
      logError('AuthContext:recordBubbleBreak', e);
    }
  }, [authUser]);

  return (
    <AuthContext.Provider value={{
      authUser, isLoggedIn, loading, selectedSources, hasConfiguredSources,
      bookmarkedIds, toggleBookmark, recordBubbleBreak, isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
